import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { authService } from '../../../utils/authService';
import { Box, Typography } from "@mui/material";
import { MaterialReactTable, useMaterialReactTable, MRT_ColumnDef } from "material-react-table";
import DetailPanel from "./DetailPanel";
import { useLocation } from "react-router-dom";
interface Bug {
  sl_id: number;
  studentname: string;
  issue_description: string;
  image_path: string;
  issue_status: string;
  comments?: {
    [key: string]: {
      comment: string;
      role: string;
      timestamp: string;
    };
  };
}


const Tickets: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const [bugs, setBugs] = useState<Bug[]>([]);
  const [studentName, setStudentName] = useState<string>("");
  const location=useLocation();
  const studentID= location.state?.student_id;
  const fetchBugs = async () => {
    if (!studentID) {
      navigate('admin/tickets');
      return;
    }

    try {
      const bugsResponse = await authService.get(`/admin/tickets/${studentID}/`);
      setBugs(bugsResponse.data.map((bug: Bug) => ({ ...bug })));
      if (bugsResponse.data.length > 0) {
        setStudentName(bugsResponse.data[0].studentname || "");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBugs();
  }, [studentID]);

  const columns = useMemo<MRT_ColumnDef<Bug>[]>(
    () => [
      {
        accessorKey: "sl_id",
        header: "Ticket no",
      },
      {
        accessorKey: "issue_type",
        header: "Issue Type",
      },
      {
         accessorKey: "reported_time",
        header: "Reported Date",
        Cell: ({ cell }) => {
          const date = new Date(cell.getValue<string>());
          return date.toLocaleDateString('en-GB', {
            timeZone: 'UTC',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          });
        }
      },
      {
        accessorKey: "resolved_time",
        header: "Solved Date",
        Cell: ({ cell }) => {
          const resolvedDate = cell.getValue<string | null>();
          return resolvedDate
            ? new Date(resolvedDate).toLocaleDateString()
            : "Not resolved";
        },
      },
      {
        accessorKey: "issue_status",
        header: "Status",
        Cell: ({ cell }) => (
          <Box
            component="span"
            sx={{
              color: cell.getValue<string>() === "Pending" ? "red" : "green",
              fontWeight: "bold",
            }}
          >
            {cell.getValue<string>()}
          </Box>
        ),
      },
    ],
    []
  );

  const table = useMaterialReactTable<Bug>({
    columns,
    data: bugs,
    enableExpanding: true,
    initialState: {
      sorting: [
        {
          id: "issue_status",
          desc: false,
        },
      ],
    },
    muiTablePaperProps: {
      sx: {
        borderRadius: "10px",
      },
    },
    muiTopToolbarProps: {
      sx: { borderBottom: "2px solid rgba(0,0,0,0.2)" },
    },
    muiTableContainerProps: {
      sx: { height: "calc(100vh - 225px)" },
    },
    renderDetailPanel: ({ row }) => (
      <DetailPanel row={{ original: row.original }} onCommentAdded={fetchBugs} />
    ),
    renderTopToolbarCustomActions: () => (
      <Typography variant="h5" gutterBottom>
        Student Name: {studentName}
      </Typography>
    ),
  });

  return (
    <>
      {loading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            minWidth: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
        >
          <Spinner animation="border" role="status" />
        </div>
      )}
      <Box sx={{ marginTop: "1%", marginRight: "0.7%" }}>
        <MaterialReactTable table={table} />
      </Box>
    </>
  );
};

export default Tickets;
